export interface DiscountInfo {
  discountedPrice: number;
  originalPrice: number;
  discountPerUnit?: number;
  discountType?: "amount" | "percentage";
  discountValue?: number;
  discountMode?: "static" | "tiered";
  discountTierMinQuantity?: number;
}

/**
 * Calculate effective discount for a product at given location and quantity
 */
export function calculateDiscountForProduct({
  product,
  selectedVariantOptions,
  locationId,
  quantity = 1,
  basePrice,
  isPluginEnabled,
}: {
  product: any;
  selectedVariantOptions?: { optionName: string; optionValue: string }[];
  locationId?: string;
  quantity?: number;
  basePrice?: number;
  isPluginEnabled?: (pluginId: string) => boolean;
}): DiscountInfo {
  const discountsEnabled = isPluginEnabled?.("discounts-promos");

  // If plugin is disabled or no location, return original price
  if (!discountsEnabled || !locationId) {
    return {
      discountedPrice: basePrice ?? 0,
      originalPrice: basePrice ?? 0,
    };
  }

  const config = getDiscountConfigForSelection(
    product,
    selectedVariantOptions,
    locationId,
  );
  if (!config) {
    return {
      discountedPrice: basePrice ?? 0,
      originalPrice: basePrice ?? 0,
    };
  }
  const originalPrice = basePrice ?? 0;
  let effectiveType: "amount" | "percentage" = config.type;
  let effectiveValue: number = config.value;
  let tierMinQuantity: number | undefined;
  if (
    config.mode === "tiered" &&
    Array.isArray(config.tiers) &&
    config.tiers.length > 0
  ) {
    const sorted = [...config.tiers].sort(
      (a, b) => a.minQuantity - b.minQuantity,
    );
    const tier = sorted
      .filter((t) => quantity >= t.minQuantity)
      .sort((a, b) => b.minQuantity - a.minQuantity)[0];

    if (tier) {
      effectiveType = tier.type ?? config.type;
      effectiveValue = tier.value;
      tierMinQuantity = tier.minQuantity;
    }
  }
  let discountPerUnit = 0;
  let discountValue = effectiveValue;
  if (
    discountValue === undefined ||
    discountValue === null ||
    isNaN(discountValue)
  ) {
    discountValue = 0;
  }
  if (effectiveType === "amount") {
    discountPerUnit = Math.min(originalPrice, Math.max(0, discountValue));
  } else {
    discountPerUnit = Math.min(
      originalPrice,
      Math.max(0, originalPrice * (discountValue / 100)),
    );
  }

  // Round to 2 decimals (currency)
  discountPerUnit = Math.round(discountPerUnit * 100) / 100;
  const discountedPrice = Math.max(
    0,
    Math.round((originalPrice - discountPerUnit) * 100) / 100,
  );

  return {
    discountedPrice,
    originalPrice,
    discountPerUnit: discountPerUnit > 0 ? discountPerUnit : undefined,
    discountType: discountPerUnit > 0 ? effectiveType : undefined,
    discountValue: discountPerUnit > 0 ? discountValue : undefined,
    discountMode: discountPerUnit > 0 ? config.mode : undefined,
    discountTierMinQuantity: discountPerUnit > 0 ? tierMinQuantity : undefined,
  };
}

/**
 * Get discount config for a product selection (mirrors backend logic)
 */
export function getDiscountConfigForSelection(
  product: any,
  selectedVariantOptions?: { optionName: string; optionValue: string }[],
  locationId?: string,
):
  | {
      mode: "static" | "tiered";
      type: "amount" | "percentage";
      value: number;
      tiers?: Array<{
        minQuantity: number;
        type?: "amount" | "percentage";
        value: number;
      }>;
    }
  | undefined {
  // Priority 1: Variant-country discount (highest priority)
  if (
    selectedVariantOptions &&
    product?.variantCombinations &&
    product.variantCombinations.length > 0 &&
    locationId
  ) {
    const combinationParts = selectedVariantOptions
      .map((selected) => `${selected.optionName}:${selected.optionValue}`)
      .sort()
      .join("|");

    const combination = product.variantCombinations.find(
      (combo: any) => combo.combination === combinationParts,
    );

    const comboLocationPricing = combination?.locationPricing?.find(
      (lp: any) => String(lp.locationId) === String(locationId),
    );

    if (comboLocationPricing?.discount) {
      return comboLocationPricing.discount;
    }
  }

  // Priority 2: Product-country discount
  if (locationId) {
    const productLocationPricing = product?.locationPricing?.find(
      (lp: any) => String(lp.locationId) === String(locationId),
    );
    if (productLocationPricing?.discount) {
      return productLocationPricing.discount;
    }
  }

  // Priority 3: Variant-level discount (no country specificity)
  if (
    selectedVariantOptions &&
    product?.variantCombinations &&
    product.variantCombinations.length > 0
  ) {
    const combinationParts = selectedVariantOptions
      .map((selected) => `${selected.optionName}:${selected.optionValue}`)
      .sort()
      .join("|");

    const combination = product.variantCombinations.find(
      (combo: any) => combo.combination === combinationParts,
    );

    if (combination?.discount) {
      return combination.discount;
    }
  }

  // Priority 4: Product-level discount (final fallback)
  if (product?.discount) {
    return product.discount;
  }

  return undefined;
}
